<script setup lang="ts">
  import { computed, toRef, watch } from "vue";
  import Button from "primevue/button";
  import ProgressBar from "primevue/progressbar";
  import CustomTag from "./CustomTag.vue";
  import { useRapportPdf, type RapportPdfState } from "../composables/useRapportPdf";
  import { useResponsive } from "../composables/useResponsive";
  import { inspectieTypeLabels, type InspectieType } from "../../../../types";

  /**
   * The inspectierapport PDF of the active scope, prominently on the rapport
   * detail page: one primary button that always does the right thing (download,
   * or make the newest PDF first and then download), with the document's state
   * spelled out next to it. On mobile the button also sits in a fixed bar at
   * the bottom of the screen. Other rapport documents go in the #extra slot.
   */
  const props = defineProps<{
    rapportId: string;
    inspectieType: InspectieType;
    rapportNr: string | null;
  }>();

  const { isMobile } = useResponsive();

  const {
    state,
    completedAt,
    lastChange,
    userInitiated,
    load,
    refresh,
    download,
    downloadCurrentVersion,
    openInNewTab,
  } = useRapportPdf(toRef(props, "rapportId"), toRef(props, "inspectieType"), toRef(props, "rapportNr"));

  watch(
    () => [props.rapportId, props.inspectieType],
    () => void load(),
    { immediate: true },
  );

  const scopeLabel = computed(() => inspectieTypeLabels[props.inspectieType] || props.inspectieType);

  const formatDay = (date: Date | null) =>
    date ? new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "short", year: "numeric" }).format(date) : "";
  const formatTime = (date: Date | null) =>
    date ? new Intl.DateTimeFormat("nl-NL", { hour: "2-digit", minute: "2-digit" }).format(date) : "";

  const tags: Record<Exclude<RapportPdfState, "loading">, { color: string; label: string; icon: string }> = {
    ready: { color: "voldoet", label: "Actueel", icon: "pi pi-check" },
    outdated: { color: "warning", label: "Nieuwe versie", icon: "pi pi-refresh" },
    missing: { color: "neutraal", label: "Klaar om te maken", icon: "pi pi-file" },
    generating: { color: "nieuw", label: "Wordt gemaakt", icon: "pi pi-spin pi-spinner" },
    queued: { color: "nieuw", label: "In de wachtrij", icon: "pi pi-clock" },
    error: { color: "voldoet_niet", label: "Niet gelukt", icon: "pi pi-exclamation-triangle" },
  };

  const tag = computed(() => (state.value === "loading" ? null : tags[state.value]));

  const description = computed(() => {
    switch (state.value) {
      case "loading":
        return "Status van de PDF ophalen…";
      case "ready":
        return `Het volledige rapport met alle constateringen, metingen en foto's. Bijgewerkt op ${formatDay(completedAt.value)} om ${formatTime(completedAt.value)}.`;
      case "outdated":
        return `Het rapport is aangepast op ${formatDay(lastChange.value)}. We maken eerst de nieuwste PDF; de download start daarna vanzelf.`;
      case "missing":
        return "We maken de PDF zodra je op downloaden klikt. Dat duurt meestal minder dan een minuut.";
      case "generating":
        return userInitiated.value
          ? "De download start vanzelf zodra de PDF klaar is. Je mag deze pagina verlaten."
          : "De PDF wordt op dit moment gemaakt.";
      case "queued":
        return "Het is druk. De PDF wordt op de achtergrond afgemaakt en verschijnt daarna hier en onder Documenten.";
      case "error":
        return "De PDF kon niet worden gemaakt. Probeer het opnieuw.";
    }
    return "";
  });

  const primary = computed(() => {
    switch (state.value) {
      case "outdated":
        return { label: "Download nieuwste", icon: "pi pi-download", action: download, outlined: false };
      case "generating":
        return { label: "PDF maken…", icon: "pi pi-download", action: download, outlined: false };
      case "queued":
        return { label: "Opnieuw kijken", icon: "pi pi-refresh", action: refresh, outlined: true };
      case "error":
        return { label: "Opnieuw proberen", icon: "pi pi-refresh", action: download, outlined: false };
      default:
        return { label: "Download PDF", icon: "pi pi-download", action: download, outlined: false };
    }
  });

  const primaryBusy = computed(() => state.value === "generating" || state.value === "loading");
</script>

<template>
  <section
    class="rapport-pdf-card"
    aria-labelledby="rapport-pdf-title"
  >
    <div class="pdf-main">
      <div
        class="pdf-thumb"
        aria-hidden="true"
      >
        <div class="pdf-thumb-band"><span /></div>
        <div class="pdf-thumb-lines">
          <span class="line-title" />
          <span class="line-sub" />
          <span class="line-block" />
          <span />
          <span />
          <span class="line-short" />
        </div>
        <span class="pdf-thumb-badge">PDF</span>
      </div>

      <div class="pdf-body">
        <div class="pdf-title-row">
          <h2
            id="rapport-pdf-title"
            class="pdf-title"
          >
            Inspectierapport {{ scopeLabel }}
          </h2>
          <CustomTag
            v-if="tag"
            :color="tag.color"
            :label="tag.label"
            :icon="tag.icon"
          />
        </div>
        <p
          class="pdf-description"
          aria-live="polite"
        >
          {{ description }}
        </p>
        <button
          v-if="state === 'outdated'"
          type="button"
          class="pdf-link"
          @click="downloadCurrentVersion"
        >
          Of download de versie van {{ formatDay(completedAt) }}
        </button>
        <ProgressBar
          v-if="state === 'generating'"
          mode="indeterminate"
          class="pdf-progress"
          aria-label="PDF wordt gemaakt"
        />
      </div>

      <div class="pdf-actions">
        <Button
          :label="primary.label"
          :icon="primary.icon"
          :loading="primaryBusy"
          :disabled="primaryBusy"
          :outlined="primary.outlined"
          :severity="primary.outlined ? 'secondary' : undefined"
          size="large"
          class="pdf-primary"
          @click="primary.action"
        />
        <Button
          v-if="state === 'ready'"
          label="Openen in nieuw tabblad"
          icon="pi pi-external-link"
          severity="secondary"
          text
          size="small"
          @click="openInNewTab"
        />
        <Button
          v-else-if="state === 'queued'"
          label="Opnieuw aanvragen"
          severity="secondary"
          text
          size="small"
          @click="download"
        />
      </div>
    </div>

    <div
      v-if="$slots.extra"
      class="pdf-extra"
    >
      <p class="pdf-extra-title">Andere documenten bij dit rapport</p>
      <slot name="extra" />
    </div>
  </section>

  <div
    v-if="isMobile"
    class="pdf-mobile-bar"
  >
    <Button
      :label="state === 'ready' || state === 'missing' ? 'Download rapport (PDF)' : primary.label"
      :icon="primary.icon"
      :loading="primaryBusy"
      :disabled="primaryBusy"
      :outlined="primary.outlined"
      :severity="primary.outlined ? 'secondary' : undefined"
      size="large"
      class="w-full"
      @click="primary.action"
    />
  </div>
</template>

<style scoped>
  .rapport-pdf-card {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }

  .pdf-main {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 1.5rem;
  }

  .pdf-thumb {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 5.25rem;
    height: 6.75rem;
    overflow: hidden;
    border: 1px solid var(--p-background-300);
    border-radius: 2px;
    background: #ffffff;
    box-shadow: 0 6px 16px rgba(20, 24, 27, 0.08);
  }

  .pdf-thumb-band {
    display: flex;
    align-items: center;
    height: 1rem;
    padding: 0 0.4rem;
    background: #14181b;
  }

  .pdf-thumb-band span {
    width: 1.75rem;
    height: 0.25rem;
    background: var(--p-primary-500);
  }

  .pdf-thumb-lines {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    padding: 0.5rem 0.45rem;
  }

  .pdf-thumb-lines span {
    height: 3px;
    background: #e3e7ed;
  }

  .pdf-thumb-lines .line-title {
    width: 3.5rem;
    height: 5px;
    background: #14181b;
  }

  .pdf-thumb-lines .line-sub {
    width: 2.5rem;
    height: 4px;
    background: #c3cbd5;
  }

  .pdf-thumb-lines .line-block {
    height: 1.2rem;
    margin-top: 0.15rem;
    background: #e3f7ea;
  }

  .pdf-thumb-lines .line-short {
    width: 2.8rem;
  }

  .pdf-thumb-badge {
    position: absolute;
    right: 0;
    bottom: 0;
    padding: 2px 5px;
    background: #c62828;
    color: #ffffff;
    font-size: 0.55rem;
    font-weight: 700;
  }

  .pdf-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
  }

  .pdf-title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.625rem;
  }

  .pdf-title {
    margin: 0;
    color: var(--p-background-900);
    font-size: 1.2rem;
    font-weight: 700;
  }

  .pdf-description {
    margin: 0;
    color: var(--p-background-600);
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .pdf-link {
    align-self: flex-start;
    padding: 0;
    border: 0;
    background: none;
    color: var(--p-background-700);
    font-size: 0.85rem;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }

  .pdf-link:hover {
    color: var(--p-background-900);
  }

  .pdf-progress {
    height: 4px;
    margin-top: 0.25rem;
  }

  .pdf-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.25rem;
    flex-shrink: 0;
    width: 14rem;
  }

  .pdf-primary {
    min-height: 3rem;
    font-weight: 700;
  }

  .pdf-extra {
    padding: 0.5rem 0.75rem 0.5rem;
    border-top: 1px solid var(--p-background-200);
    background: var(--p-background-50);
  }

  .pdf-extra-title {
    margin: 0;
    padding: 0.25rem 0.75rem 0.25rem;
    color: var(--p-background-500);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .pdf-mobile-bar {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 40;
    padding: 0.75rem calc(env(safe-area-inset-right) + 1rem) calc(env(safe-area-inset-bottom) + 0.75rem)
      calc(env(safe-area-inset-left) + 1rem);
    border-top: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    box-shadow: 0 -6px 16px rgba(20, 24, 27, 0.06);
  }

  @media (max-width: 767px) {
    .pdf-main {
      flex-wrap: wrap;
      gap: 1rem;
      padding: 1rem;
    }

    .pdf-thumb {
      width: 3rem;
      height: 3.9rem;
    }

    .pdf-thumb-lines {
      display: none;
    }

    .pdf-body {
      flex-basis: calc(100% - 4rem);
    }

    .pdf-actions {
      width: 100%;
    }

    /* The fixed bottom bar carries the primary action on mobile. */
    .pdf-primary {
      display: none;
    }
  }
</style>
